import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import { Box, Typography, CircularProgress, Dialog, useMediaQuery } from "@mui/material";
import {
  CheckCircle,
  AlertCircle,
  Download,
  Share2,
  Copy,
  X,
  User,
  Zap,
  GraduationCap,
  Briefcase,
  FileText,
  ChevronRight,
} from "lucide-react";
import sh from "bundles/common/utils/sh";
import SquarehubLoader from "bundles/common/components/SquarehubLoader";
import trackEvent from "bundles/common/utils/trackEvent";

const cardSx = {
  bgcolor: "hsl(0, 0%, 100%)",
  borderRadius: "16px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  overflow: "hidden",
};

const btnPrimarySx = {
  component: "button",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  py: "12px",
  px: "24px",
  borderRadius: "12px",
  border: "none",
  cursor: "pointer",
  bgcolor: "hsl(153, 68%, 25%)",
  color: "hsl(0, 0%, 100%)",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  fontSize: "13px",
  width: "100%",
  transition: "background-color 0.15s",
  "&:hover:not(:disabled)": { bgcolor: "hsl(153, 68%, 30%)" },
  "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
};

const btnSecondarySx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  py: "12px",
  px: "24px",
  borderRadius: "12px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  cursor: "pointer",
  bgcolor: "transparent",
  color: "hsl(178, 10%, 45%)",
  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
  fontSize: "13px",
  width: "100%",
  transition: "background-color 0.15s",
  "&:hover:not(:disabled)": { bgcolor: "hsla(160, 10%, 96%, 0.3)" },
  "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
};

const ResumeTab = ({ onNavigate }) => {
  const intl = useIntl();
  const f = (id, values) => intl.formatMessage({ id: `candidate.resume_tab.${id}` }, values);
  const currentCandidate = useSelector((state) => state.current_candidate);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [resumeUrl, setResumeUrl] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:599px)");

  useEffect(() => {
    trackEvent("candidate_resume", "opened");
  }, []);

  // The generated PDF reflects the profile at generation time — drop the
  // cached URL whenever the candidate data changes so we never share a stale CV.
  useEffect(() => {
    setResumeUrl(null);
  }, [currentCandidate]);

  if (!currentCandidate) return null;

  const steps = [
    {
      code: "profile",
      icon: User,
      label: f("steps.profile_label"),
      description: f("steps.profile_desc"),
      target: "headline",
      completed: !!(
        currentCandidate.first_name &&
        currentCandidate.last_name &&
        currentCandidate.email &&
        currentCandidate.presentation
      ),
    },
    {
      code: "skills",
      icon: Zap,
      label: f("steps.skills_label"),
      description: f("steps.skills_desc"),
      target: "skills",
      completed: !!(
        currentCandidate.hard_skills?.length > 0 &&
        currentCandidate.soft_skills?.length > 0 &&
        currentCandidate.language_skills?.length > 0
      ),
    },
    {
      code: "trainings",
      icon: GraduationCap,
      label: f("steps.trainings_label"),
      description: f("steps.trainings_desc"),
      target: "education",
      completed: !!(currentCandidate.trainings?.length > 0),
    },
    {
      code: "experiences",
      icon: Briefcase,
      label: f("steps.experiences_label"),
      description: f("steps.experiences_desc"),
      target: "experience",
      completed: !!(currentCandidate.experiences?.length > 0),
    },
  ];

  const completedCount = steps.filter((s) => s.completed).length;
  const allDone = completedCount === steps.length;
  const percentage = Math.round((completedCount / steps.length) * 100);

  const progressColor =
    percentage === 100
      ? "hsl(153, 68%, 25%)"
      : percentage >= 50
        ? "hsl(40, 90%, 50%)"
        : "hsl(0, 65%, 51%)";

  const fetchResume = (onSuccess) => {
    if (resumeUrl) {
      onSuccess(resumeUrl);
      return;
    }
    setLoading(true);
    sh.get("/generate_resume", {
      authenticity_token: authenticityToken,
      candidate: currentCandidate,
    })
      .then((res) => {
        if (res?.data?.resume_url) {
          setResumeUrl(res.data.resume_url);
          onSuccess(res.data.resume_url);
        } else {
          enqueueSnackbar(f("toast.generation_error"), { variant: "error" });
        }
      })
      .catch(() => {
        enqueueSnackbar(f("toast.generation_error"), { variant: "error" });
      })
      .finally(() => setLoading(false));
  };

  const handleDownload = () => {
    fetchResume((url) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = `${currentCandidate.first_name}_CV.pdf`;
      link.dispatchEvent(
        new MouseEvent("click", { bubbles: true, cancelable: true, view: window })
      );
    });
  };

  const handleShare = () => {
    setShareOpen(true);
    fetchResume(() => {});
  };

  const copyUrl = async () => {
    if (!resumeUrl) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(resumeUrl);
      ok = true;
    } catch {
      const ta = document.createElement("textarea");
      ta.value = resumeUrl;
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      ta.remove();
    }
    enqueueSnackbar(f(ok ? "toast.link_copied" : "toast.generation_error"), { variant: ok ? "success" : "error" });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>

      {/* ── Progress header ── */}
      <Box sx={{ ...cardSx, p: "24px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            mb: "16px",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "20px",
                color: "hsl(178, 93%, 10%)",
                mb: "4px",
              }}
            >
              {f("title")}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "hsl(178, 10%, 45%)",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              }}
            >
              {allDone
                ? f("profile_complete")
                : f("steps_remaining", { count: steps.length - completedCount })}
            </Typography>
          </Box>

          <Box
            sx={{
              textAlign: "right",
              flexShrink: 0,
              ml: "16px",
            }}
          >
            <Typography
              sx={{
                fontSize: "26px",
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                color: progressColor,
                lineHeight: 1,
                mb: "2px",
              }}
            >
              {completedCount}/{steps.length}
            </Typography>
            <Typography
              sx={{
                fontSize: "10px",
                color: "hsl(178, 10%, 45%)",
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {f("steps_label")}
            </Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        <Box
          sx={{
            width: "100%",
            height: "6px",
            bgcolor: "hsla(160, 10%, 90%, 0.5)",
            borderRadius: "9999px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "6px",
              borderRadius: "9999px",
              bgcolor: progressColor,
              width: `${percentage}%`,
              transition: "width 0.6s ease",
            }}
          />
        </Box>
      </Box>

      {/* ── Two-column grid ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: "16px",
          alignItems: "start",
        }}
      >
        {/* ─── Left: Checklist ─── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <Box
                key={step.code}
                sx={{
                  ...cardSx,
                  p: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  border: step.completed
                    ? "1px solid hsla(153, 68%, 25%, 0.3)"
                    : "1px solid hsla(160, 10%, 90%, 0.6)",
                }}
              >
                {/* Step icon */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "12px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: step.completed
                      ? "hsla(153, 68%, 25%, 0.1)"
                      : "hsla(160, 10%, 96%, 0.5)",
                  }}
                >
                  {step.completed ? (
                    <CheckCircle size={18} color="hsl(153, 68%, 25%)" />
                  ) : (
                    <Icon size={18} color="hsl(178, 10%, 45%)" />
                  )}
                </Box>

                {/* Step text */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                      fontSize: "13px",
                      color: "hsl(178, 93%, 10%)",
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "hsl(178, 10%, 45%)",
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>

                {/* Badge / CTA */}
                {step.completed ? (
                  <Box
                    sx={{
                      px: "10px",
                      py: "3px",
                      borderRadius: "9999px",
                      bgcolor: "hsla(153, 68%, 25%, 0.1)",
                      color: "hsl(153, 68%, 25%)",
                      fontSize: "10px",
                      fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f("completed")}
                  </Box>
                ) : (
                  <Box
                    component="button"
                    onClick={() => onNavigate && onNavigate("profile", null, { profileSection: step.target })}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      border: "none",
                      bgcolor: "transparent",
                      cursor: "pointer",
                      color: "hsl(153, 68%, 25%)",
                      fontSize: "11px",
                      fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      "&:hover": { color: "hsl(153, 68%, 35%)" },
                    }}
                  >
                    {f("complete_cta")}
                    <ChevronRight size={12} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ─── Right: Actions + Contents ─── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Generate card */}
          <Box sx={{ ...cardSx, p: "20px" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                mb: "16px",
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  bgcolor: "hsla(153, 68%, 25%, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <FileText size={20} color="hsl(153, 68%, 25%)" />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    fontSize: "15px",
                    color: "hsl(178, 93%, 10%)",
                  }}
                >
                  {f("generate_title")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "hsl(178, 10%, 45%)",
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  }}
                >
                  {f("generate_subtitle")}
                </Typography>
              </Box>
            </Box>

            {/* Warning when steps incomplete */}
            {!allDone && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  p: "12px",
                  borderRadius: "10px",
                  bgcolor: "hsla(40, 90%, 50%, 0.08)",
                  border: "1px solid hsla(40, 90%, 50%, 0.2)",
                  mb: "16px",
                }}
              >
                <AlertCircle
                  size={14}
                  color="hsl(40, 90%, 45%)"
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "hsl(178, 93%, 10%)",
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  }}
                >
                  {f("generate_warning")}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Download button */}
              <Box
                component="button"
                disabled={!allDone || loading}
                onClick={handleDownload}
                sx={btnPrimarySx}
              >
                {loading ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <Download size={14} />
                )}
                {f("download_pdf")}
              </Box>

              {/* Share button */}
              <Box
                component="button"
                disabled={!allDone || loading}
                onClick={handleShare}
                sx={btnSecondarySx}
              >
                <Share2 size={14} />
                {f("share_cv")}
              </Box>
            </Box>
          </Box>

          {/* What's included card */}
          <Box sx={{ ...cardSx, p: "20px" }}>
            <Typography
              sx={{
                fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                fontSize: "13px",
                color: "hsl(178, 93%, 10%)",
                mb: "14px",
              }}
            >
              {f("contents_title")}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { Icon: User, label: f("contents.personal") },
                { Icon: Briefcase, label: f("contents.experiences") },
                { Icon: GraduationCap, label: f("contents.trainings") },
                { Icon: Zap, label: f("contents.skills") },
              ].map(({ Icon, label }) => (
                <Box
                  key={label}
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "8px",
                      bgcolor: "hsla(160, 10%, 96%, 0.5)",
                      border: "1px solid hsla(160, 10%, 90%, 0.6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={13} color="hsl(153, 68%, 25%)" />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "hsl(178, 10%, 45%)",
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Share modal ── */}
      <Dialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        fullScreen={isMobile}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : "20px" } }}
      >
        {shareOpen && (
          <Box
            sx={{
              bgcolor: "hsl(0, 0%, 100%)",
              p: "28px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              boxSizing: "border-box",
            }}
          >
            {/* Modal header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                    fontSize: "18px",
                    color: "hsl(178, 93%, 10%)",
                  }}
                >
                  {f("share_title")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "hsl(178, 10%, 45%)",
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    mt: "4px",
                  }}
                >
                  {f("share_subtitle")}
                </Typography>
              </Box>
              <Box
                component="button"
                onClick={() => setShareOpen(false)}
                sx={{
                  border: "none",
                  bgcolor: "transparent",
                  cursor: "pointer",
                  p: "4px",
                  ml: "12px",
                  display: "flex",
                  color: "hsl(178, 10%, 45%)",
                  "&:hover": { color: "hsl(178, 93%, 10%)" },
                }}
              >
                <X size={16} />
              </Box>
            </Box>

            {/* Tips */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {[
                f("tips.tip1"),
                f("tips.tip2"),
                f("tips.tip3"),
              ].map((text) => (
                <Box
                  key={text}
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <CheckCircle
                    size={14}
                    color="hsl(153, 68%, 25%)"
                    style={{ flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "hsl(178, 10%, 45%)",
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* URL field */}
            <Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontFamily: "Segoe UI Bold, sans-serif, system-ui",
                  color: "hsl(178, 10%, 45%)",
                  mb: "6px",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {f("share_link_label")}
              </Typography>

              {loading ? (
                <SquarehubLoader size={28} centered />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    p: "10px 14px",
                    borderRadius: "12px",
                    border: "1px solid hsla(160, 10%, 90%, 0.6)",
                    bgcolor: "hsla(160, 10%, 96%, 0.3)",
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: "12px",
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(178, 93%, 10%)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {resumeUrl || f("generating")}
                  </Typography>
                  {resumeUrl && (
                    <Box
                      component="button"
                      onClick={copyUrl}
                      sx={{
                        border: "none",
                        bgcolor: "transparent",
                        cursor: "pointer",
                        p: 0,
                        display: "flex",
                        color: "hsl(153, 68%, 25%)",
                        "&:hover": { color: "hsl(153, 68%, 35%)" },
                      }}
                    >
                      <Copy size={14} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>

          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default ResumeTab;
